/**
 * Case Path State Model — Pure TypeScript (zero React)
 *
 * Defines the 6-stage case progression spine, posture-driven deliverables,
 * and verification debt tracking for the attestation gate.
 */

export type Stage = 'setup' | 'foundation' | 'method' | 'calculate' | 'attest' | 'deliver';
export type Posture = 'claim' | 'negotiation' | 'litigation';
export type ActivePath = 'time_element' | 'property' | 'business_value_loss' | 'recovery';

export interface VerificationDebt {
  engines_expected: number;
  engines_run: number;
  blockers: number;
  warnings: number;
}

export interface CaseSpineState {
  stage: Stage;
  posture: Posture;
  activePaths: Set<ActivePath>;
  verificationDebt: VerificationDebt;
}

export interface GateResult {
  satisfied: boolean;
  reason: string;
}

export interface GateInputs {
  humanAttested?: boolean;
  [key: string]: unknown;
}

const STAGE_ORDER: Stage[] = ['setup', 'foundation', 'method', 'calculate', 'attest', 'deliver'];

/**
 * Returns the successor stage in the fixed progression.
 * setup → foundation → method → calculate → attest → deliver → null
 * Returns null after 'deliver' (terminal).
 */
export function nextStage(stage: Stage): Stage | null {
  const idx = STAGE_ORDER.indexOf(stage);
  if (idx === -1 || idx === STAGE_ORDER.length - 1) {
    return null;
  }
  return STAGE_ORDER[idx + 1];
}

/**
 * Evaluates whether the current stage's exit gate is satisfied.
 *
 * Hard rule: attest stage with blockers > 0 is always blocked.
 * Other stages are permissive (satisfied: true) unless gateInputs contains
 * explicit failure signals.
 */
export function stageGateSatisfied(
  state: CaseSpineState,
  gateInputs: GateInputs
): GateResult {
  // Critical safety gate: attest stage must have zero blockers
  if (state.stage === 'attest' && state.verificationDebt.blockers > 0) {
    return {
      satisfied: false,
      reason: `Cannot proceed: ${state.verificationDebt.blockers} blockers must be resolved before attestation`,
    };
  }

  // For non-attest stages, check for explicit failure signals in gateInputs
  if (gateInputs?.failure === true || gateInputs?.blocked === true) {
    return {
      satisfied: false,
      reason: String(gateInputs.reason ?? 'Gate explicitly blocked by input'),
    };
  }

  return {
    satisfied: true,
    reason: 'Gate satisfied',
  };
}

/**
 * Returns the list of deliverable IDs available for a given posture.
 * Returns a new array on each call to prevent external mutation.
 */
export function deliverMenuFor(posture: Posture): string[] {
  switch (posture) {
    case 'claim':
      return ['proof_of_loss', 'wb2', 'demand_letter'];
    case 'negotiation':
      return ['settlement_brief', 'mediation_package'];
    case 'litigation':
      return ['expert_report', 'bates_exhibits', 'euo_prep', 'ror_response', 'court_pdfs'];
    default:
      return [];
  }
}
